import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";

export default function SubpageLayout({
  category,
  title,
  highlightWord,
  subtitle,
  heroImage,
  heroBadge,
  problem,
  workflow,
  features,
  stats,
  faq,
  ctaSource,
  seoTitle,
  seoDescription,
  seoPath,
}) {
  const { openLeadCapture } = useLeadCapture();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [seoPath]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <Seo
        title={seoTitle || category}
        description={seoDescription}
        path={seoPath || "/"}
        image={heroImage}
      />
      <Navbar />
      <main className="pt-28">
        {/* Breadcrumb */}
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <nav className="flex items-center text-xs text-slate-500 dark:text-slate-400 gap-1">
            <Link to="/" className="hover:text-emerald-600 dark:hover:text-emerald-400">Hem</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 dark:text-slate-200">{category}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-16 md:pb-28 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-[420px] w-[420px] blob-emerald opacity-60 pointer-events-none" />
          <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-700 dark:text-emerald-300">
                {heroBadge}
              </div>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] font-bold tracking-tight text-slate-900 dark:text-white">
                {title}{" "}
                {highlightWord && (
                  <span className="relative inline-block">
                    <span className="relative z-10">{highlightWord}</span>
                    <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 dark:bg-emerald-500/30 -z-0 rounded" />
                  </span>
                )}
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-xl">
                {subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => openLeadCapture(ctaSource)}
                  data-testid="subpage-primary-cta"
                  className="h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)] hover:-translate-y-0.5 transition-all"
                >
                  Boka en demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    data-testid="subpage-pricing-link"
                    className="h-12 px-6 rounded-xl border-slate-300 dark:border-slate-700 font-semibold"
                  >
                    Se priser
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800">
                <img src={heroImage} alt={category} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* Problem */}
        <section className="py-20 bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
          <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Problemet
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold leading-tight text-slate-900 dark:text-white">
                {problem.title}
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4">
              {problem.paragraphs.map((p, i) => (
                <p key={i} className="text-slate-600 dark:text-slate-300 leading-relaxed text-lg">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Så fungerar det
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                {workflow.title}
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">{workflow.intro}</p>
            </div>
            <ol className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {workflow.steps.map((s, i) => (
                <li
                  key={s.title}
                  className="relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6"
                >
                  <div className="font-display text-5xl font-bold text-emerald-500/20">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {s.desc}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Stats */}
        {stats && (
          <section className="py-16 bg-slate-900 text-white">
            <div className="mx-auto max-w-7xl px-6 lg:px-12 grid sm:grid-cols-3 gap-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-5xl font-bold text-emerald-400">{s.value}</div>
                  <div className="mt-2 text-sm text-slate-300 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Features bento */}
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Inkluderat
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Alla funktioner du behöver
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 transition-all hover:-translate-y-0.5 hover:border-emerald-500/40 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display mt-4 text-lg font-semibold text-slate-900 dark:text-white">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        {faq && (
          <section className="py-20 border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
            <div className="mx-auto max-w-4xl px-6 lg:px-12">
              <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-white">Vanliga frågor</h2>
              <dl className="mt-10 space-y-4">
                {faq.map((q) => (
                  <div
                    key={q.q}
                    className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6"
                  >
                    <dt className="font-display text-lg font-semibold text-slate-900 dark:text-white flex items-start gap-2">
                      <Check className="mt-1 h-4 w-4 text-emerald-500 shrink-0" />
                      {q.q}
                    </dt>
                    <dd className="mt-2 ml-6 text-slate-600 dark:text-slate-300 leading-relaxed">{q.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white p-10 md:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-dotted opacity-20" />
              <div className="relative max-w-2xl">
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                  Är du redo att förenkla din vardag?
                </h2>
                <p className="mt-4 text-slate-300 text-lg">
                  Boka en kostnadsfri 30-minuters demo. Vi visar systemet, svarar på frågor och
                  hjälper dig avgöra om Hundify passar din verksamhet.
                </p>
                <Button
                  onClick={() => openLeadCapture(ctaSource)}
                  data-testid="subpage-bottom-cta"
                  className="mt-8 h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)]"
                >
                  Boka demo gratis <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
